<template>
  <div class="page-wrapper admin-stats-sheet admin-stats-sheet--cols-4">
    <div class="stats-row">
      <div class="mini-stat" v-for="s in eqStats" :key="s.label">
        <div class="mini-stat-icon" :style="{ background: s.bg, color: s.color }"><component :is="s.icon" /></div>
        <div><p class="mini-stat-val">{{ s.value }}</p><p class="mini-stat-label">{{ s.label }}</p></div>
      </div>
    </div>

    <div class="page-toolbar admin-list-toolbar">
      <div class="page-toolbar-left">
        <div class="filter-tabs">
          <button v-for="tab in tabs" :key="tab.v" class="filter-tab" :class="{active: activeTab===tab.v}" @click="activeTab=tab.v">{{ tab.l }}</button>
        </div>
      </div>
      <div class="page-toolbar-right">
        <div class="view-toggle">
          <button class="view-btn" :class="{active: viewMode==='grid'}" @click="setViewMode('grid')"><el-icon><Grid /></el-icon></button>
          <button class="view-btn" :class="{active: viewMode==='list'}" @click="setViewMode('list')"><el-icon><List /></el-icon></button>
        </div>
        <button class="btn-primary" @click="openAdd"><el-icon><Plus /></el-icon><span>新增器材</span></button>
      </div>
    </div>

    <div v-if="viewMode==='grid'" class="equipment-grid">
      <div class="eq-card" v-for="eq in filteredEquipment" :key="eq.id">
        <div class="eq-card-img-wrap" :style="{background: eq.iconBg}">
          <img v-if="eq.imgUrl" :src="eq.imgUrl" class="eq-card-img" :alt="eq.name" @error="onImgError($event, eq)" />
          <span v-else class="eq-emoji">{{ eq.emoji }}</span>
        </div>
        <div class="eq-body">
          <div class="eq-header">
            <h3 class="eq-name">{{ eq.name }}</h3>
            <span class="eq-status" :class="eq.statusCls">{{ eq.statusLabel }}</span>
          </div>
          <p class="eq-brand">{{ eq.brand }} · {{ eq.model }}</p>
          <div class="eq-meta">
            <span><el-icon><Location /></el-icon>{{ eq.location }}</span>
            <span><el-icon><Calendar /></el-icon>{{ eq.lastMaint }}</span>
          </div>
          <div class="eq-footer">
            <div class="eq-footer-actions">
              <button class="act-btn view" @click="viewDetail(eq)" style="width:auto;padding:0 10px;gap:4px;font-size:12px;">
                <el-icon><View /></el-icon><span>详情</span>
              </button>
            </div>
            <div class="eq-actions">
              <button class="act-btn edit" @click="editEq(eq)"><el-icon><Edit /></el-icon></button>
              <button class="act-btn del" @click="deleteEq(eq)"><el-icon><Delete /></el-icon></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="table-card">
      <el-table :data="filteredEquipment" style="width:100%" :header-cell-style="headerStyle">
        <el-table-column type="index" label="#" width="55" />
        <el-table-column label="图片" width="80">
          <template #default="{row}">
            <div class="eq-thumb-wrap">
              <img v-if="row.imgUrl" :src="row.imgUrl" class="eq-thumb" :alt="row.name" @error="onImgError($event, row)" />
              <div v-else class="eq-thumb-fallback" :style="{background: row.iconBg}">
                <span>{{ row.emoji }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="器材名称" min-width="160">
          <template #default="{row}">
            <div class="eq-cell"><span>{{row.name}}</span></div>
          </template>
        </el-table-column>
        <el-table-column prop="brand" label="品牌" width="120" />
        <el-table-column prop="location" label="位置" width="120" />
        <el-table-column label="状态" width="110">
          <template #default="{row}"><span class="eq-status" :class="row.statusCls">{{row.statusLabel}}</span></template>
        </el-table-column>
        <el-table-column prop="lastMaint" label="上次维护" width="130" />
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{row}">
            <div class="action-btns">
              <button class="act-btn view" @click="viewDetail(row)"><el-icon><View /></el-icon></button>
              <button class="act-btn edit" @click="editEq(row)"><el-icon><Edit /></el-icon></button>
              <button class="act-btn del" @click="deleteEq(row)"><el-icon><Delete /></el-icon></button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog v-model="showDialog" :title="editing ? '编辑器材' : '新增器材'" width="520px">
      <el-form :model="form" label-width="90px">
        <el-form-item label="器材名称"><el-input v-model="form.name" /></el-form-item>
        <el-form-item label="品牌"><el-input v-model="form.brand" /></el-form-item>
        <el-form-item label="型号"><el-input v-model="form.model" /></el-form-item>
        <el-form-item label="放置位置"><el-input v-model="form.location" /></el-form-item>
        <el-form-item label="状态">
          <el-select v-model="form.status" style="width:100%">
            <el-option label="正常" :value="1" />
            <el-option label="维修中" :value="0" />
            <el-option label="已报废" :value="2" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <button class="btn-secondary" @click="showDialog=false">取消</button>
          <button class="btn-primary" @click="saveEq">保存</button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Grid, List, Edit, Delete, Location, Calendar, Tools, TrendCharts, Warning, View } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import api from '@/api/request';
import adminListViewModeMixin from '@/mixins/adminListViewMode';
import { equipmentCoverUrl } from '@/utils/media';

const emojis = { '跑步机': '🏃', '哑铃': '🏋️', '椭圆机': '🚴', '划船机': '🚣', '器材': '🔧' };
const statusMap = { 0: { label: '维修中', cls: 'maint' }, 1: { label: '正常', cls: 'ok' }, 2: { label: '已报废', cls: 'off' } };

export default {
  name: 'EquipmentView',
  mixins: [adminListViewModeMixin],
  components: { Grid, List, Edit, Delete, Location, Calendar, Tools, TrendCharts, Warning, View },
  data() {
    return {
      activeTab: 'all',
      showDialog: false,
      editing: null,
      form: { name: '', brand: '', model: '', location: '', status: 1 },
      tabs: [{ l: '全部', v: 'all' }, { l: '正常', v: '1' }, { l: '维修中', v: '0' }, { l: '已报废', v: '2' }],
      eqStats: [
        { label: '器材总数', value: '-', icon: 'Tools', bg: 'rgba(0,98,255,0.1)', color: '#0062FF' },
        { label: '正常使用', value: '-', icon: 'TrendCharts', bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
        { label: '维修中', value: '-', icon: 'Warning', bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
        { label: '已报废', value: '-', icon: 'Delete', bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
      ],
      equipment: [],
      headerStyle: { background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontWeight: 500 },
    };
  },
  computed: {
    filteredEquipment() {
      if (this.activeTab === 'all') return this.equipment;
      return this.equipment.filter(e => String(e.statusCode) === this.activeTab);
    }
  },
  mounted() {
    this.loadEquipment();
  },
  watch: {
    activeTab() { this.loadEquipment(); }
  },
  methods: {
    async loadEquipment() {
      try {
        const params = { pageNumber: 1, pageSize: 100 };
        if (this.activeTab !== 'all') params.status = this.activeTab;
        const res = await api.getEquipmentPage(params);
        if (res.data && res.data.code === 200) {
          const list = res.data.data?.records || [];
          this.equipment = list.map(e => ({
            ...e,
            id: e.equipmentId,
            emoji: Object.keys(emojis).find(k => e.name?.includes(k)) ? emojis[Object.keys(emojis).find(k => e.name?.includes(k))] : '🔧',
            iconBg: e.status === 1 ? 'rgba(16,185,129,0.1)' : e.status === 0 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
            statusLabel: statusMap[e.status]?.label || '未知',
            statusCls: statusMap[e.status]?.cls || 'off',
            statusCode: e.status,
            lastMaint: e.lastMaintenance || '-',
            usage: 0,
            imgUrl: equipmentCoverUrl(e),
            imgFailed: false,
          }));
          this.eqStats[0].value = String(list.length);
          this.eqStats[1].value = String(list.filter(e => e.status === 1).length);
          this.eqStats[2].value = String(list.filter(e => e.status === 0).length);
          this.eqStats[3].value = String(list.filter(e => e.status === 2).length);
        }
      } catch (e) {}
    },
    viewDetail(eq) {
      this.$router.push({ name: 'equipmentDetail', params: { id: eq.equipmentId } });
    },
    onImgError(event, row) {
      row.imgUrl = '';
    },
    openAdd() {
      this.editing = null;
      this.form = { name: '', brand: '', model: '', location: '', status: 1 };
      this.showDialog = true;
    },
    editEq(eq) {
      this.editing = eq;
      this.form = {
        equipmentId: eq.equipmentId,
        name: eq.name,
        brand: eq.brand,
        model: eq.model,
        location: eq.location,
        status: eq.statusCode,
        imageUrl: eq.imageUrl || null,
        videoUrl: eq.videoUrl || null,
      };
      this.showDialog = true;
    },
    async deleteEq(eq) {
      try {
        await ElMessageBox.confirm('确定删除器材 ' + eq.name + '？', '提示', { type: 'warning' });
        const res = await api.removeEquipment(eq.equipmentId);
        if (res.data?.code === 200) {
          ElMessage.success('删除成功');
          this.loadEquipment();
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败');
      }
    },
    async saveEq() {
      try {
        const form = { ...this.form };
        let res;
        if (this.editing) {
          res = await api.updateEquipment(form);
        } else {
          res = await api.saveEquipment(form);
        }
        if (res.data?.code === 200) {
          ElMessage.success('保存成功');
          this.showDialog = false;
          this.loadEquipment();
        } else {
          ElMessage.error(res.data?.message || '保存失败');
        }
      } catch (e) {
        ElMessage.error('保存失败');
      }
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  padding: 0;
  background: var(--bg-base, #f9fafb);
  min-height: 100vh;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  height: 38px;
  background: var(--gradient-primary, linear-gradient(135deg, #0062FF 0%, #0036D9 100%));
  color: white;
  border: none;
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 6px -1px rgba(0, 98, 255, 0.2);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 8px -1px rgba(0, 98, 255, 0.3);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  height: 38px;
  background: var(--bg-gray, #F3F4F6);
  color: var(--text-primary, #111827);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-secondary:hover {
  border-color: var(--primary-blue, #0062FF);
  color: var(--primary-blue, #0062FF);
}

.mini-stat {
  background: var(--bg-white, #ffffff);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-lg, 12px);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all 0.3s;
}

.mini-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.mini-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.mini-stat-val {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary, #111827);
  margin: 0 0 2px;
  font-family: 'Inter', sans-serif;
}

.mini-stat-label {
  font-size: 12px;
  color: var(--text-muted, #6B7280);
  margin: 0;
}

.filter-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-tab {
  padding: 6px 16px;
  border: 1px solid var(--border-color, #E5E7EB);
  background: var(--bg-gray, #F3F4F6);
  color: var(--text-secondary, #4B5563);
  border-radius: var(--radius-sm, 6px);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.filter-tab:hover {
  border-color: var(--primary-blue, #0062FF);
  color: var(--primary-blue, #0062FF);
}

.filter-tab.active {
  background: var(--primary-blue, #0062FF);
  border-color: var(--primary-blue, #0062FF);
  color: white;
}

.view-toggle {
  display: flex;
  gap: 4px;
}

.view-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-color, #E5E7EB);
  background: var(--bg-white, #ffffff);
  color: var(--text-muted, #6B7280);
  border-radius: var(--radius-sm, 6px);
  cursor: pointer;
  transition: all 0.3s;
}

.view-btn.active {
  background: rgba(0, 98, 255, 0.1);
  color: var(--primary-blue, #0062FF);
  border-color: var(--primary-blue, #0062FF);
}

.equipment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.eq-card {
  background: var(--bg-white, #ffffff);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  display: flex;
  gap: 16px;
  transition: all 0.3s;
}

.eq-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.eq-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

.eq-card-img-wrap {
  width: 88px;
  height: 88px;
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  flex-shrink: 0;
  overflow: hidden;
}

.eq-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-md, 8px);
}

.eq-thumb-wrap {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eq-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: var(--radius-sm, 6px);
  border: 1px solid var(--border-color, #E5E7EB);
}

.eq-thumb-fallback {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-sm, 6px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.eq-body {
  flex: 1;
  min-width: 0;
}

.eq-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
}

.eq-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #111827);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.eq-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.eq-status.ok {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.eq-status.maint {
  background: rgba(245, 158, 11, 0.1);
  color: #F59E0B;
}

.eq-status.off {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.eq-brand {
  font-size: 13px;
  color: var(--text-muted, #6B7280);
  margin: 0 0 12px;
}

.eq-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary, #4B5563);
  margin-bottom: 16px;
}

.eq-meta span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.eq-meta .el-icon {
  color: var(--text-muted, #6B7280);
}

.eq-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--border-color, #E5E7EB);
  padding-top: 12px;
}

.eq-footer-actions {
  flex: 1;
}

.eq-usage {
  flex: 1;
  padding-right: 16px;
}

.eq-usage-label {
  font-size: 12px;
  color: var(--text-muted, #6B7280);
  margin-bottom: 4px;
  display: block;
}

.usage-bar {
  height: 6px;
  background: var(--bg-gray, #F3F4F6);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}

.usage-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.usage-bar-inline {
  width: 60px;
  height: 6px;
  background: var(--bg-gray, #F3F4F6);
  border-radius: 3px;
  overflow: hidden;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

.eq-usage-pct {
  font-size: 12px;
  color: var(--text-primary, #111827);
  font-weight: 500;
}

.eq-actions, .action-btns {
  display: flex;
  gap: 6px;
}

.act-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm, 6px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.act-btn.view {
  background: rgba(0, 98, 255, 0.08);
  color: #0062FF;
  display: flex;
  align-items: center;
}

.act-btn.view:hover {
  background: #0062FF;
  color: white;
}

.act-btn.edit {
  background: rgba(245, 158, 11, 0.1);
  color: #F59E0B;
}

.act-btn.edit:hover {
  background: #F59E0B;
  color: white;
}

.act-btn.del {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.act-btn.del:hover {
  background: #EF4444;
  color: white;
}

.table-card {
  background: var(--bg-white, #ffffff);
  border: 1px solid var(--border-color, #E5E7EB);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  width: 100%;
  overflow-x: auto;
}

.eq-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.eq-cell-emoji {
  font-size: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 适配移动端和响应式布局 */
@media (max-width: 768px) {
  .equipment-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .equipment-grid { gap: 12px; }
  .eq-footer { flex-direction: column; align-items: flex-start; gap: 10px; }
  .eq-usage { padding-right: 0; width: 100%; }
  .dialog-footer { flex-direction: column-reverse; }
  .dialog-footer button { width: 100%; justify-content: center; min-height: 40px; }
}

</style>


